import { useEffect, useState, useCallback } from "react";
import { useQuery } from "@app/utils";
import { Language } from "@app/provider";
import { LinkSource } from "./useTokenizedLinks";
import { useTokenizedLinks } from ".";
import { NotificationTemplateName } from "./useGeneratedTokenizedLink";
import { useSignupIntent } from "./useSignupIntent";
import { useSetUserLanguage } from "./useSetUserLanguage";

export const useTokenSourceFromQuery = (): {
  tokenSource?: LinkSource;
  tokenNotificationType?: NotificationTemplateName;
  token: string | null;
  lang: string | null;
} => {
  const query = useQuery();
  const token = query.get("token");
  const [tokenSource, setTokenSource] = useState<LinkSource>();
  const [tokenNotificationType, setTokenNotificationType] = useState<
    NotificationTemplateName | undefined
  >(undefined);
  const { getTokenSource, getTokenLanguage } = useTokenizedLinks();
  const setUserLanguage = useSetUserLanguage();
  const { intentToken, handleReverificationRequired } = useSignupIntent();

  const langFromQuery = query.get("lang");

  // Applied up front so the DOB screen renders in the link's language, not English.
  const applyTokenLanguage = useCallback(async () => {
    if (!token) return;

    const language = langFromQuery ?? (await getTokenLanguage(token));
    setUserLanguage((language ?? undefined) as Language | undefined);
  }, [token, langFromQuery]);

  useEffect(() => {
    applyTokenLanguage();
  }, [applyTokenLanguage]);

  const fetchTokenSource = useCallback(async () => {
    if (!token) return;

    try {
      const data = await getTokenSource(token, intentToken);

      if (data?.notificationType) {
        setTokenNotificationType(data?.notificationType);
      }

      setTokenSource(data?.source);
    } catch (err) {
      // Only a presented, rejected proof errors here: send it back to DOB.
      // Any other error leaves the source undefined.
      handleReverificationRequired(err);
    }
  }, [token, intentToken, handleReverificationRequired]);

  useEffect(() => {
    fetchTokenSource();
  }, [fetchTokenSource]);

  return {
    tokenSource,
    tokenNotificationType,
    token,
    lang: langFromQuery,
  };
};
